/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  educationalService,
  type AddEducationalContent,
} from "@src/@api-services/educational-content";
import type {
  IEducationalContent,
  IEducationalContentSearchOptions,
} from "../types";

interface EducationalContentState {
  data: IEducationalContent[];
  total: number;
  params: IEducationalContentSearchOptions;
  selectedEducationalContent: IEducationalContent | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getEducationalContent = createAsyncThunk(
  "educationalContent/getEducationalContent",
  async (params: IEducationalContentSearchOptions, { rejectWithValue }) => {
    try {
      const response = await educationalService.getEducationalContent(params);
      return {
        params,
        data: response.data.data.data as IEducationalContent[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch educational content"
      );
    }
  }
);

export const getEducationalContentDetails = createAsyncThunk(
  "educationalContent/getEducationalContentDetails",
  async (id: number, { rejectWithValue }) => {
    try {
      const response =
        await educationalService.getEducationalContentDetails(id);
      return response.data.data as IEducationalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch educational content details"
      );
    }
  }
);

export const addEducationalContent = createAsyncThunk(
  "educationalContent/addEducationalContent",
  async (data: AddEducationalContent, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await educationalService.addEducationalContent(data);
      const state = getState() as any;
      await dispatch(getEducationalContent(state.educationalContent.params));
      return response.data.data as IEducationalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add educational content"
      );
    }
  }
);

export const updateEducationalContent = createAsyncThunk(
  "educationalContent/updateEducationalContent",
  async (
    {
      id,
      educationalData,
    }: { id: string | number; educationalData: Partial<AddEducationalContent> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await educationalService.updateEducationalContent(
        id,
        educationalData
      );
      const state = getState() as any;
      await dispatch(getEducationalContent(state.educationalContent.params));
      return response.data.data as IEducationalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update educational content"
      );
    }
  }
);

export const deleteEducationalContent = createAsyncThunk(
  "educationalContent/deleteEducationalContent",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await educationalService.deleteEducationalContent(id);
      const state = getState() as any;
      await dispatch(getEducationalContent(state.educationalContent.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete educational content"
      );
    }
  }
);

const initialState: EducationalContentState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedEducationalContent: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
};

export const educationalContentSlice = createSlice({
  name: "educationalContent",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedEducationalContent: (state) => {
      state.selectedEducationalContent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEducationalContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEducationalContent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getEducationalContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getEducationalContentDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getEducationalContentDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedEducationalContent = action.payload;
      })
      .addCase(getEducationalContentDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedEducationalContent = null;
      })
      .addCase(addEducationalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addEducationalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addEducationalContent.rejected, (state) => {
        // Drawer shows toast — do not flip list TableErrorState (roles pattern).
        state.actionLoading = false;
      })
      .addCase(updateEducationalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateEducationalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateEducationalContent.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteEducationalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteEducationalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteEducationalContent.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearError, clearSelectedEducationalContent } =
  educationalContentSlice.actions;

export default educationalContentSlice.reducer;
